import React, { useEffect, useMemo, useRef, useState } from "react";
import { Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import useCoachStyles from "./coachStyles";
import PropTypes from "prop-types";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import CoachActionsDialog from "../CoachActionsDialog";

const useStyles = makeStyles(theme => ({
  messageBox: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    background: theme.mode === "light" ? "#f6f8fb" : "#16202a",
    border: `1px solid ${theme.mode === "light" ? "#d9e3ef" : "#25313d"}`,
    marginBottom: theme.spacing(2)
  },
  noteRow: {
    border: `1px solid ${theme.mode === "light" ? "#dde7f1" : "#22303a"}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    background: theme.mode === "light" ? "#fff" : "#18232c"
  },
  noteHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1)
  },
  noteMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center"
  },
  noteReply: {
    marginLeft: theme.spacing(3),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1.5)
  },
  preservedText: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 220px",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  actionsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  emptyState: {
    opacity: 0.8,
    padding: theme.spacing(1, 0)
  }
}));

const flattenNotes = (notes = [], level = 0) => {
  const rows = [];
  notes.forEach(note => {
    rows.push({ ...note, level });
    if (Array.isArray(note.replies) && note.replies.length) {
      rows.push(...flattenNotes(note.replies, level + 1));
    }
  });
  return rows;
};

const getMessagePreview = message => {
  if (!message) return "";
  if (message.body && String(message.body).trim()) return String(message.body).trim();
  if (message.mediaType) return `Mensaje de tipo ${message.mediaType}`;
  return "Mensaje sin contenido de texto";
};

const MessageCoachNotesDialog = ({
  open,
  onClose,
  message,
  ticket,
  currentUser,
  initialReplyNote,
  onChanged
}) => {
  const classes = useStyles();
  const coachClasses = useCoachStyles();
  const inputRef = useRef(null);
  const selection = useRef({ start: 0, end: 0 });
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const replyRecipient = note => Number(note.authorUserId) === Number(currentUser?.id)
    ? String(note.targetUserId || "") : String(note.authorUserId || "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [manageId, setManageId] = useState(null);

  const isAdmin = ["admin", "super"].includes(String(currentUser?.profile || ""));
  const flattenedNotes = useMemo(() => flattenNotes(notes), [notes]);

  const loadData = async () => {
    if (!message?.id || !open) {
      return;
    }

    setLoading(true);
    try {
      const requests = [api.get(`/messages/${message.id}/coach-notes`)];
      if (isAdmin) {
        requests.push(api.get("/users/list"));
      }

      const [notesResponse, usersResponse] = await Promise.all(requests);
      setNotes(Array.isArray(notesResponse?.data?.notes) ? notesResponse.data.notes : []);
      setUsers(Array.isArray(usersResponse?.data) ? usersResponse.data : []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setNoteText("");
      setReplyTarget(null);
      setTargetUserId("");
      setEmojiAnchor(null);
      return;
    }

    setReplyTarget(initialReplyNote || null);
    if (initialReplyNote) setTargetUserId(replyRecipient(initialReplyNote));
    loadData();
  }, [open, message?.id, initialReplyNote?.id]);

  useEffect(() => {
    if (!open || replyTarget?.id || initialReplyNote?.id) {
      return;
    }

    if (!isAdmin) {
      setTargetUserId("");
      return;
    }

    setTargetUserId("");
  }, [open, isAdmin, replyTarget?.id, message?.id]);

  const handleSave = async () => {
    if (!message?.id || !noteText.trim() || (!replyTarget && (!targetUserId || Number(targetUserId) === Number(currentUser?.id)))) {
      return;
    }

    setSaving(true);
    try {
      await api.post(`/coach-actions/messages/${message.id}/notes`, {
        note: noteText.trim(),
        targetUserId: replyTarget?.id ? undefined : targetUserId || undefined,
        parentNoteId: replyTarget?.id || undefined,
        kind: replyTarget ? "reply" : "guidance"
      });

      setNoteText("");
      setReplyTarget(initialReplyNote || null);
      if (initialReplyNote) setTargetUserId(replyRecipient(initialReplyNote));
      if (!replyTarget?.id && isAdmin) {
        setTargetUserId("");
      }
      await loadData();
      window.dispatchEvent(new Event("coach-updated"));
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkRead = async noteId => {
    try {
      await api.post(`/coach-actions/notes/${noteId}/read`);
      await loadData();
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      toastError(error);
    }
  };

  const prepareReply = note => {
    setReplyTarget(note);
    setTargetUserId(replyRecipient(note));
    const textarea = document.getElementById("message-coach-note-text");
    if (textarea) {
      textarea.focus();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper" PaperProps={{ className: coachClasses.root }}>
      <DialogTitle>Anotacion interna del mensaje</DialogTitle>
      <DialogContent dividers>
        <div className={classes.messageBox}>
          <Typography variant="subtitle2" gutterBottom>
            Mensaje base
          </Typography>
          <Typography variant="body2" className={classes.preservedText}>
            {getMessagePreview(message)}
          </Typography>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : flattenedNotes.length ? (
          flattenedNotes.map(note => {
            const isPendingForMe =
              Number(note.targetUserId || 0) === Number(currentUser?.id || 0) &&
              !note.readAt &&
              note.status !== "resolved";

            return (
              <div
                key={note.id}
                className={`${classes.noteRow} ${note.level > 0 ? classes.noteReply : ""}`}
              >
                <div className={classes.noteHeader}>
                  <div>
                    <Typography variant="subtitle2">
                      {note.authorUser?.name || "Usuario"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(note.createdAt).toLocaleString("es-CO")}
                    </Typography>
                  </div>
                  <div className={classes.noteMeta}>
                    {note.targetUser?.name ? (
                      <Chip size="small" label={`Para: ${note.targetUser.name}`} />
                    ) : null}
                    <Chip
                      size="small"
                      color={note.status === "resolved" ? "primary" : "default"}
                      label={note.status === "resolved" ? "Resuelta" : "Abierta"}
                    />
                    {isPendingForMe ? (
                      <Chip size="small" style={{ background: "#ffd54f" }} label="Pendiente para mi" />
                    ) : null}
                  </div>
                </div>
                <Typography variant="body2" className={classes.preservedText}>
                  {note.note}
                </Typography>
                <div className={classes.actionsRow}>
                  <div>
                    <Button size="small" color="primary" onClick={() => prepareReply(note)}>
                      Responder
                    </Button>
                    {!note.readAt && Number(note.targetUserId) === Number(currentUser?.id) ? (
                      <Button size="small" color="default" onClick={() => handleMarkRead(note.id)}>
                        Marcar leida
                      </Button>
                    ) : null}
                    {note.status !== "resolved" ? (
                      <Button size="small" color="secondary" onClick={() => setManageId(note.id)}>
                        Resolver
                      </Button>
                    ) : null}
                    <Button size="small" variant="outlined" onClick={() => setManageId(note.id)}>Gestion / historial</Button>
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    {note.readAt ? `Leida: ${new Date(note.readAt).toLocaleString("es-CO")}` : "Sin lectura"}
                  </Typography>
                </div>
              </div>
            );
          })
        ) : (
          <Typography variant="body2" className={classes.emptyState}>
            Aun no hay anotaciones internas para este mensaje.
          </Typography>
        )}

        <Divider style={{ margin: "16px 0" }} />

        {replyTarget ? (
          <Chip
            color="primary"
            label={`Respondiendo anotacion #${replyTarget.id} de ${replyTarget.authorUser?.name || "usuario"}`}
            onDelete={() => setReplyTarget(null)}
          />
        ) : null}

        <div className={classes.formGrid}>
          <div>
          <TextField
            inputRef={inputRef}
            onSelect={() => { const input = inputRef.current; if (input) selection.current = { start: input.selectionStart, end: input.selectionEnd }; }}
            id="message-coach-note-text"
            label={replyTarget ? "Respuesta interna" : "Nueva anotacion interna"}
            placeholder="Escriba la guia, correccion o comentario interno"
            multiline
            minRows={4}
            variant="outlined"
            value={noteText}
            onChange={event => setNoteText(event.target.value)}
            fullWidth
          />
          <Button variant="outlined" onMouseDown={event => event.preventDefault()} onClick={event => setEmojiAnchor(event.currentTarget)}>Insertar emoji</Button>
          <Popover open={Boolean(emojiAnchor)} anchorEl={emojiAnchor} onClose={() => setEmojiAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Picker title="Emojis" emoji="speech_balloon" showPreview={false} showSkinTones={false} i18n={{ search: "Buscar", notfound: "Sin resultados", categories: { search: "Resultados", recent: "Recientes", people: "Personas", nature: "Naturaleza", foods: "Comida", activity: "Actividades", places: "Viajes", objects: "Objetos", symbols: "Simbolos", flags: "Banderas", custom: "Personalizados" } }} onSelect={emoji => {
              const { start, end } = selection.current;
              setNoteText(text => text.slice(0, start) + emoji.native + text.slice(end));
              setEmojiAnchor(null);
              requestAnimationFrame(() => { const input = inputRef.current; if (input) { input.focus(); input.setSelectionRange(start + emoji.native.length, start + emoji.native.length); } });
            }} />
          </Popover>
          </div>

          <FormControl variant="outlined" fullWidth disabled={!isAdmin || Boolean(replyTarget?.id)}>
            <InputLabel id="message-coach-target-label">Dirigida a</InputLabel>
            <Select
              labelId="message-coach-target-label"
              value={targetUserId}
              onChange={event => setTargetUserId(event.target.value)}
              label="Dirigida a"
            >
              <MenuItem value="">
                <em>Seleccione a quien va dirigida</em>
              </MenuItem>
              {targetUserId && !users.some(item => String(item.id) === targetUserId) && <MenuItem value={targetUserId}>
                {Number(replyTarget?.authorUserId) === Number(currentUser?.id) ? replyTarget?.targetUser?.name || `Usuario #${targetUserId}` : replyTarget?.authorUser?.name || `Usuario #${targetUserId}`}
              </MenuItem>}
              {users.map(userItem => (
                <MenuItem key={userItem.id} value={String(userItem.id)} disabled={Number(userItem.id) === Number(currentUser?.id)}>
                  {userItem.name}{userItem.email ? ` (${userItem.email})` : ""}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
              {replyTarget
                ? "La respuesta se enviara al otro participante de esta anotacion."
                : targetUserId
                  ? "Solo el usuario dirigido y administracion veran esta anotacion."
                  : "Seleccione explicitamente al destinatario antes de enviar. No se usara el responsable del chat automaticamente."}
            </Typography>
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cerrar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || !noteText.trim() || (!replyTarget && (!targetUserId || Number(targetUserId) === Number(currentUser?.id)))}
        >
          {saving ? "Guardando..." : replyTarget ? "Enviar respuesta" : "Guardar anotacion"}
        </Button>
      </DialogActions>
      {manageId && <CoachActionsDialog key={`${currentUser?.companyId}:${currentUser?.id}:${manageId}`} noteId={manageId} currentUser={currentUser} onClose={() => setManageId(null)} onChanged={() => { loadData(); onChanged?.(); }} />}
    </Dialog>
  );
};

MessageCoachNotesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.object,
  ticket: PropTypes.object,
  currentUser: PropTypes.object,
  initialReplyNote: PropTypes.object,
  onChanged: PropTypes.func
};

export default MessageCoachNotesDialog;
